'use client';

import Link from 'next/link';
import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import { cn } from '@/lib/utils';

interface LogoProps {
  className?: string;
}

/**
 * EVDEV logo as SVG text. Links to the homepage; once mounted on `/` it
 * turns inert so the page doesn't navigate to itself.
 *
 * The DOM shape must NOT branch on `usePathname()`: the prerendered shell
 * of one URL can be served under another (soft-404 fallbacks, CDN shell
 * reuse), and an element-type swap (<span> at home vs <a> elsewhere) made
 * hydration throw React #418 on every production homepage load. The
 * server always renders the same <Link>; the home-page inert state is
 * applied only after mount, when the client pathname is authoritative.
 */
export function Logo({ className }: LogoProps) {
  const pathname = usePathname();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const inert = mounted && pathname === '/';

  return (
    <Link
      href="/"
      aria-label="EVDEV - Go to homepage"
      aria-current={inert ? 'page' : undefined}
      tabIndex={inert ? -1 : undefined}
      onClick={(e) => {
        if (inert) e.preventDefault();
      }}
      className={cn(
        'text-[24px] font-normal uppercase tracking-[-0.04em] text-foreground',
        inert
          ? 'cursor-default select-none'
          : 'transition-colors hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
        className,
      )}
    >
      EVDEV
    </Link>
  );
}
